import classNames from 'classnames'
import { FunctionComponent, useEffect, useId, useRef, useState } from 'react'
import orderSummaryStyles from '../OrderSummary/OrderSummary.module.css'
import styles from './TaxLineItemLabel.module.css'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import StringToReactNode from '../StringToReactNode/StringToReactNode'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import TXT from '@/messages'

type Props = {
  label: string
}

/**
 * Returns the tax-waived message that should be shown in the order summary.
 *
 * When tax-waived messaging is not enabled, or no tax-waived messages are configured,
 * this returns `null`. When enabled, it selects the with-tax or no-tax message based
 * on whether the cart currently has taxes applied.
 *
 * @param {MetaData[] | null} sellerMeta - Seller metadata used to determine tax-waived messaging.
 * @param {number} taxesTotal - The cart tax total used to choose which message to display.
 * @returns {string | null} The tax-waived message to display, or `null` when no message should render.
 */
export const getTaxWaivedMessageToDisplay = (
  taxWaivedEnabled: boolean,
  taxWaivedMessageNoTax: string | null,
  taxWaivedMessageWithTax: string | null,
  taxesTotal: number,
): string | null => {
  if (!taxWaivedEnabled) return null
  if (!taxWaivedMessageNoTax && !taxWaivedMessageWithTax) return null

  if (taxesTotal > 0) {
    return taxWaivedMessageWithTax
  }

  return taxWaivedMessageNoTax
}

const TaxLineItemLabel: FunctionComponent<Props> = ({ label }: Props) => {
  const { cart } = useCart()
  const { taxWaivedEnabled, taxWaivedMessageNoTax, taxWaivedMessageWithTax } = useSeller()

  const taxWaivedMessageToDisplay = getTaxWaivedMessageToDisplay(
    taxWaivedEnabled,
    taxWaivedMessageNoTax,
    taxWaivedMessageWithTax,
    cart.taxesTotal,
  )

  const taxInfoRef = useRef<HTMLDetailsElement | null>(null)
  const taxTooltipId = useId()
  const [isTaxTooltipOpen, setIsTaxTooltipOpen] = useState(false)
  const [isTaxTooltipHovered, setIsTaxTooltipHovered] = useState(false)
  const [isTaxTooltipFocused, setIsTaxTooltipFocused] = useState(false)
  const isTaxTooltipVisible = isTaxTooltipOpen || isTaxTooltipHovered || isTaxTooltipFocused

  // handle closing the tax tooltip when clicking outside of it
  useEffect(() => {
    if (!isTaxTooltipOpen) return undefined

    const handlePointerDownOutside = (event: PointerEvent): void => {
      if (!taxInfoRef.current?.contains(event.target as Node)) {
        setIsTaxTooltipOpen(false)
        setIsTaxTooltipHovered(false)
        setIsTaxTooltipFocused(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDownOutside)

    return () => document.removeEventListener('pointerdown', handlePointerDownOutside)
  }, [isTaxTooltipOpen])

  return (
    <div className={styles.TaxLineItemLabel}>
      <h2 className={classNames(
        orderSummaryStyles.GeneralTitle,
        orderSummaryStyles.MinorTitle,
      )}
      >
        {label}
      </h2>
      {taxWaivedMessageToDisplay && (
        <details
          className={styles.TaxInfo}
          open={isTaxTooltipVisible}
          ref={taxInfoRef}
          onPointerEnter={() => setIsTaxTooltipHovered(true)}
          onPointerLeave={() => setIsTaxTooltipHovered(false)}
          onFocus={() => setIsTaxTooltipFocused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setIsTaxTooltipFocused(false)
            }
          }}
        >
          <summary
            aria-controls={taxTooltipId}
            aria-expanded={isTaxTooltipVisible}
            className={classNames('btn-unstyled', styles.TaxInfoButton)}
            aria-label={TXT.a11y.showTaxInfo}
            onClick={(event) => {
              event.preventDefault()
              setIsTaxTooltipOpen((currentValue) => !currentValue)
            }}
          >
            <span className={styles.TaxInfoIcon} aria-hidden="true">i</span>
          </summary>
          <div
            className={styles.TaxTooltip}
            id={taxTooltipId}
            role="tooltip"
          >
            <StringToReactNode
              incomingString={taxWaivedMessageToDisplay}
              className={styles.TaxTooltipContent}
              useTypoStandard={false}
            />
          </div>
        </details>
      )}
    </div>
  )
}

export default TaxLineItemLabel
